import {Box, Chip, CircularProgress, Divider, IconButton, Stack, Typography, useTheme} from "@mui/joy";
import {useEffect} from "react";
import {BadgePercent, Copy, Gift, Link2, Users} from "lucide-react";
import Card from "./Card";
import {useAsync} from "../hooks/useAsyncResult";
import {useFeedback} from "../hooks/useFeedback";
import {getMyReferrals} from "../services/api/referrals";
import {referralShareUrl} from "../services/referral";
import {ReferralCredit} from "../types/referral";
import {activePalette, applyTransparency} from "../theme";
import {useTranslation} from "react-i18next";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace"

/** Credit origins and statuses read as words, so each one carries the key it is written with */
const CREDIT_ORIGIN_KEYS: { [origin in ReferralCredit["origin"]]: string } = {
    referrer: "creditOriginReferrer",
    referred: "creditOriginReferred"
}

const CREDIT_STATUS_KEYS: { [status: string]: string } = {
    available: "creditStatusAvailable",
    consumed: "creditStatusConsumed",
    expired: "creditStatusExpired"
}

const formatDate = (value?: number) => value ? new Date(value).toLocaleDateString() : "—"

const ReferralCard = () => {
    const theme = useTheme()
    const palette = activePalette(theme)
    const feedback = useFeedback()
    const {t} = useTranslation()
    const referralAsyncResult = useAsync(getMyReferrals, true)

    useEffect(() => {
        referralAsyncResult.execute()
    }, [])

    const summary = referralAsyncResult.data?.response?.[0]
    const shareUrl = summary ? referralShareUrl(summary.code) : ""

    const copy = async (value: string, message: string) => {
        try {
            await navigator.clipboard.writeText(value)
            feedback.showFeedback(message, "success")
        } catch (e) {
            feedback.showFeedback(t("copyUnavailable"), "danger")
        }
    }

    const creditStatusColor = (credit: ReferralCredit) =>
        credit.status === "available" ? "success" : credit.status === "consumed" ? "primary" : "neutral"

    return <Card>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
            <Typography level={"title-lg"} startDecorator={<Gift/>}>
                {t("inviteFriends")}
            </Typography>
            {referralAsyncResult.loading && <CircularProgress size={"sm"}/>}
        </Stack>

        {summary && <Typography level={"body-sm"}>
            {t("referralPitch", {
                referred: summary.program.referredDiscountPercent,
                referrer: summary.program.referrerDiscountPercent
            })}
        </Typography>}

        {referralAsyncResult.error && <Typography level={"body-sm"} color={"danger"}>
            {referralAsyncResult.error}
        </Typography>}

        {summary && <>
            {/* Code */}
            <Stack gap={1}>
                <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                    {t("yourCode")}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}
                       sx={{
                           paddingX: 2,
                           paddingY: 1.5,
                           borderRadius: "13px",
                           background: applyTransparency(palette.primary["500"], 0.15),
                           border: `1px solid ${applyTransparency(palette.primary["600"], 0.4)}`
                       }}>
                    <Typography sx={{
                        fontFamily: MONO,
                        fontSize: {xs: "1.1rem", sm: "1.4rem"},
                        fontWeight: 700,
                        letterSpacing: {xs: "0.12em", sm: "0.25em"},
                        overflowWrap: "anywhere",
                        color: palette.primary["700"]
                    }}>
                        {summary.code}
                    </Typography>
                    <IconButton variant={"plain"} size={"sm"}
                                onClick={() => copy(summary.code, t("referralCodeCopied"))}>
                        <Copy/>
                    </IconButton>
                </Stack>
            </Stack>

            {/* Share link */}
            <Stack direction={"row"} alignItems={"center"} gap={1}>
                <Link2 style={{color: palette.neutral["400"]}}/>
                <Typography level={"body-xs"} noWrap sx={{flex: 1, minWidth: 0, fontFamily: MONO}}>
                    {shareUrl}
                </Typography>
                <IconButton variant={"plain"} size={"sm"}
                            onClick={() => copy(shareUrl, t("invitationLinkCopied"))}>
                    <Copy/>
                </IconButton>
            </Stack>

            <Divider/>

            {/* Stats */}
            <Box display={"grid"} gridTemplateColumns={"repeat(3, minmax(0, 1fr))"} gap={{xs: 1, sm: 2}}>
                {[
                    {
                        label: t("friendsJoined"),
                        value: `${summary.referralsCount}`,
                        icon: <Users style={{color: palette.primary["600"]}}/>
                    },
                    {
                        label: t("creditsAvailable"),
                        value: `${summary.availableCreditsCount}`,
                        icon: <Gift style={{color: "#2DDF82"}}/>
                    },
                    {
                        label: t("offNextPayment"),
                        value: `${summary.nextPaymentDiscountPercent}%`,
                        icon: <BadgePercent style={{color: "#2DDF82"}}/>
                    }
                ].map(stat => <Stack key={stat.label} alignItems={"center"} gap={0.5}>
                    {stat.icon}
                    <Typography level={"h4"}>{stat.value}</Typography>
                    <Typography level={"body-xs"} textAlign={"center"}>{stat.label}</Typography>
                </Stack>)}
            </Box>

            {/* Credits */}
            {!!summary.credits.length && <Stack gap={1}>
                <Typography level={"body-xs"} textTransform={"uppercase"} letterSpacing={"0.1em"}>
                    {t("yourDiscounts")}
                </Typography>
                {summary.credits.map(credit => <Stack key={credit._id} direction={"row"} alignItems={"center"}
                                                      justifyContent={"space-between"} gap={2}
                                                      sx={{
                                                          paddingX: 2,
                                                          paddingY: 1,
                                                          borderRadius: "13px",
                                                          background: "rgba(255,255,255,0.03)",
                                                          border: "1px solid rgba(255,255,255,0.08)"
                                                      }}>
                    <Stack>
                        <Typography level={"body-sm"}>
                            {t("percentOff", {percent: credit.discountPercent})} · {t(CREDIT_ORIGIN_KEYS[credit.origin])}
                        </Typography>
                        <Typography level={"body-xs"}>
                            {credit.status === "consumed"
                                ? t("usedOn", {date: formatDate(credit.consumedAt)})
                                : t("validUntil", {date: formatDate(credit.expiresAt)})}
                        </Typography>
                    </Stack>
                    <Chip size={"sm"} variant={"soft"} color={creditStatusColor(credit)}>
                        {t(CREDIT_STATUS_KEYS[credit.status] || credit.status)}
                    </Chip>
                </Stack>)}
            </Stack>}

            {!summary.referralsCount && <Typography level={"body-xs"}>
                {t("noOneUsedCode")}
            </Typography>}
        </>}
    </Card>
}

export default ReferralCard
